import { useMemo, useState } from 'react'
import { NoData } from 'src/components/analytics/common/NoData'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import { ChartSkeleton } from 'src/components/overview/charts/common/Skeleton'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { useLocale } from 'src/lib/hooks/useLocal'
import { truncateString } from 'src/lib/utils/helpers'

type DISCOUNT_NAME = string
type SESSION_COUNT = number
type COMPLETE_COUNT = number
type SUCCESS_RATE = string

type DiscountData = [DISCOUNT_NAME, SESSION_COUNT, COMPLETE_COUNT, SUCCESS_RATE]

const options = [
  {
    label: 'Top Discounts',
    value: 'SUCCESS_RATE'
  },
  {
    label: 'Checked out count',
    value: 'COMPLETE_COUNT'
  },
  {
    label: 'Session count',
    value: 'SESSION_COUNT'
  }
]

export default function DiscountsComparisonTable() {
  const [selectedOption, setSelectedOption] = useState<string>(options[0].value)
  const { t } = useLocale()
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.discounts_your_discounts,
      dependencies: {
        endpoints: ['/discount-conversions']
      }
    },
    refreshKey
  })

  const discountData: DiscountData[] = useMemo(() => {
    const rawData: DiscountData[] = data[0]?.data?.result?.widgetData?.[0]?.data
    if (!rawData) return []
    return rawData
  }, [data])

  const selectedData = useMemo(() => {
    if (selectedOption === 'SUCCESS_RATE') {
      return discountData.sort((a, b) => parseFloat(b[3]) - parseFloat(a[3]))
    }
    if (selectedOption === 'COMPLETE_COUNT') {
      return discountData.sort((a, b) => b[2] - a[2])
    }
    return discountData.sort((a, b) => b[1] - a[1])
  }, [discountData, selectedOption])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (discountData.length > 0) {
      return (
        <div className='w-full h-full'>
          <div className='pt-3'>
            <DropdownButton
              options={options}
              selectedOption={options.findIndex(option => option.value === selectedOption)}
              size='large'
              variant='outlined'
              handleClick={(index, value) => setSelectedOption(value)}
            />
          </div>
          <div className='bg-white rounded-xl'>
            <table className='w-full text-left'>
              <thead className='sticky top-0 bg-white'>
                <tr className='text-xs text-gray-500'>
                  <th className='py-2 max-w-10'> </th>
                  <th className='py-2 text-right'>{t('total_sessions')}</th>
                  <th className='py-2 text-right'>{t('nav_orders')}</th>
                  <th className='py-2 text-right'>{t('success_rate')}</th>
                </tr>
              </thead>
              <tbody>
                {selectedData.map((item, idx) => (
                  <>
                    <tr key={item[0] + idx} className='bg-gray-lightest text-sm'>
                      <td className='py-2 flex items-center pl-2'>{truncateString(item[0], 20)}</td>
                      <td className='py-2 text-right'>{item[1]}</td>
                      <td className='py-2 text-right'>{item[2]}</td>
                      <td className='py-2 text-right'>{item[3]}</td>
                    </tr>
                    <tr className='bg-white'>
                      <td colSpan={5} className='py-[2px]'></td>
                    </tr>
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full overflow-y-auto mt-2 pb-4'>
      {renderComponent()}
    </div>
  )
}
